import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Empty, Skeleton, Typography, Space, Tag, Button, Pagination } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import auctionService from '../../services/auctionService';
import { message } from 'antd';
import { toggleFavoriteAuction } from '../../redux/userSlice';
import AuctionCard from '../../components/Auction/AuctionCard';
import AuctionCardSkeleton from '../../components/Auction/AuctionCardSkeleton';
import AuctionSearch from '../../components/Auction/AuctionSearch';
import AuctionCountdown from '../../components/Common/AuctionCountdown';
import moment from 'moment';
import './SearchResults.css';
import { SearchOutlined, ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const SearchResults = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [filters, setFilters] = useState({
        status: 'all',
        category: 'all'
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });
    const { currentUser } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Read URL parameters when component mounts or URL changes
    useEffect(() => {
        // This useEffect is now handled by the main useEffect below
        // Keeping this for any other URL parameter logic if needed
    }, [location.search]);

    const loadSearchResults = useCallback(async (filtersToUse = null, searchValue = null, page = null) => {
        try {
            setLoading(true);
            
            const currentFilters = filtersToUse || filters;
            const currentSearchValue = searchValue !== null ? searchValue : searchInput;
            const currentPage = page !== null ? page : pagination.current;
            
            // Fetch auctions
            const auctionsResponse = await auctionService.getAllAuctions({
                ...currentFilters,
                status: currentFilters.status === 'all' ? undefined : currentFilters.status.toLowerCase(),
                category: currentFilters.category === 'all' ? undefined : currentFilters.category.toLowerCase(),
                search: currentSearchValue || undefined,
                page: currentPage,
                per_page: pagination.pageSize
            });

            // Process auctions with favorites
            const favoriteAuctions = currentUser?.favorite_auctions || [];
            const auctionsWithFavorites = auctionsResponse.data.map(auction => ({
                ...auction,
                is_favorited: favoriteAuctions.includes(auction.id)
            }));

            setAuctions(auctionsWithFavorites);
            
            // Update pagination from response
            if (auctionsResponse.meta) {
                setPagination(prev => ({
                    ...prev,
                    current: auctionsResponse.meta.current_page || prev.current,
                    total: auctionsResponse.meta.total || 0,
                    pageSize: auctionsResponse.meta.per_page || prev.pageSize
                }));
            }
        } catch (error) {
            console.error('Error loading search results:', error);
            message.error('Failed to load search results');
        } finally {
            setLoading(false);
        }
    }, [filters, searchInput, pagination.current, pagination.pageSize, currentUser?.favorite_auctions]);

    // Load search results when component mounts with URL parameters
    useEffect(() => {
        // Parse URL parameters and load search results
        const searchParams = new URLSearchParams(location.search);
        const searchQuery = searchParams.get('q');
        const statusQuery = searchParams.get('status');
        const categoryQuery = searchParams.get('category');
        const pageQuery = searchParams.get('page');

        const urlFilters = {
            status: statusQuery || 'all',
            category: categoryQuery || 'all'
        };

        // Update component state with URL parameters
        setSearchInput(searchQuery || '');
        setFilters(urlFilters);
        
        // Update pagination from URL
        if (pageQuery) {
            setPagination(prev => ({
                ...prev,
                current: parseInt(pageQuery, 10) || 1
            }));
        } else {
            setPagination(prev => ({
                ...prev,
                current: 1
            }));
        }

        // Load search results with URL parameters
        const page = pageQuery ? parseInt(pageQuery, 10) : 1;
        loadSearchResults(urlFilters, searchQuery, page);
    }, [location.search]); // Only depend on location.search, not on searchInput/filters

    // Sync local state with Redux favorites
    useEffect(() => {
        if (currentUser) {
            const favoriteAuctions = currentUser.favorite_auctions || [];
            setAuctions(prevAuctions => 
                prevAuctions.map(auction => ({
                    ...auction,
                    is_favorited: favoriteAuctions.includes(auction.id)
                }))
            );
        }
    }, [currentUser?.favorite_auctions]);

    // Removed handleFilterChange - filters only apply on search button click

    const handleSearch = (value, searchFilters = null) => {
        const filtersToUse = searchFilters || filters;
        
        setSearchInput(value);
        setFilters(filtersToUse);
        setPagination(prev => ({ ...prev, current: 1 }));
        
        const searchParams = new URLSearchParams();
        if (value) searchParams.set('q', value);
        if (filtersToUse.status !== 'all') searchParams.set('status', filtersToUse.status);
        if (filtersToUse.category !== 'all') searchParams.set('category', filtersToUse.category);
        
        navigate({
            pathname: '/search-results',
            search: searchParams.toString()
        }, { replace: true });
        
        // Load search results with the new filters and search value
        loadSearchResults(filtersToUse, value, 1);
    };

    const handleFavoriteToggle = (auctionId) => {
        // Optimistically update local state
        setAuctions(prevAuctions => 
            prevAuctions.map(auction => 
                auction.id === auctionId 
                    ? { ...auction, is_favorited: !auction.is_favorited }
                    : auction
            )
        );

        // Optimistically update Redux state
        dispatch(toggleFavoriteAuction(auctionId));
    };

    const goBackToAuctions = () => {
        navigate('/auctions');
    };

    const removeFilter = (filterType) => {
        const newFilters = { ...filters };
        let newSearchInput = searchInput;
        
        if (filterType === 'search') {
            newSearchInput = '';
        } else if (filterType === 'status') {
            newFilters.status = 'all';
        } else if (filterType === 'category') {
            newFilters.category = 'all';
        }
        
        setFilters(newFilters);
        setSearchInput(newSearchInput);
        setPagination(prev => ({ ...prev, current: 1 }));
        
        // Update URL
        const searchParams = new URLSearchParams();
        if (newSearchInput) searchParams.set('q', newSearchInput);
        if (newFilters.status !== 'all') searchParams.set('status', newFilters.status);
        if (newFilters.category !== 'all') searchParams.set('category', newFilters.category);
        
        navigate({
            pathname: '/search-results',
            search: searchParams.toString()
        }, { replace: true });
        
        // Reload search results
        loadSearchResults(newFilters, newSearchInput, 1);
    };
    
    const handlePaginationChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
        
        // Update URL with page parameter
        const searchParams = new URLSearchParams(location.search);
        if (page > 1) {
            searchParams.set('page', page);
        } else {
            searchParams.delete('page');
        }
        
        navigate({
            pathname: '/search-results',
            search: searchParams.toString()
        }, { replace: true });
        
        // Load search results with new page
        loadSearchResults(filters, searchInput, page);
    };

    return (
        <div className="search-results">
            <AuctionSearch
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                filters={filters}
                setFilters={setFilters}
                loading={loading}
                auctionCount={auctions.length}
                onSearch={handleSearch}
                title="Search Results"
                subtitle={searchInput ? `Search results for "${searchInput}"` : 'All auctions'}
            />

            {/* Active Filters Tags */}
            {(searchInput || filters.status !== 'all' || filters.category !== 'all') && (
                <div style={{ 
                    marginBottom: 24,
                    padding: '16px 20px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 8 
                    }}>
                        <Text strong style={{ 
                            fontSize: '14px', 
                            color: '#475569',
                            marginRight: 8
                        }}>
                            Active Filters:
                        </Text>
                        
                        {/* Search Query Tag */}
                        {searchInput && (
                            <Tag
                                closable
                                onClose={() => removeFilter('search')}
                                closeIcon={<CloseOutlined style={{ fontSize: '12px' }} />}
                                style={{
                                    fontSize: '13px',
                                    padding: '4px 12px',
                                    borderRadius: '16px',
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <SearchOutlined style={{ fontSize: '12px' }} />
                                Search: "{searchInput}"
                            </Tag>
                        )}
                        
                        {/* Status Filter Tag */}
                        {filters.status !== 'all' && (
                            <Tag
                                closable
                                onClose={() => removeFilter('status')}
                                closeIcon={<CloseOutlined style={{ fontSize: '12px' }} />}
                                style={{
                                    fontSize: '13px',
                                    padding: '4px 12px',
                                    borderRadius: '16px',
                                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    textTransform: 'capitalize'
                                }}
                            >
                                Status: {filters.status}
                            </Tag>
                        )}
                        
                        {/* Category Filter Tag */}
                        {filters.category !== 'all' && (
                            <Tag
                                closable
                                onClose={() => removeFilter('category')}
                                closeIcon={<CloseOutlined style={{ fontSize: '12px' }} />}
                                style={{
                                    fontSize: '13px',
                                    padding: '4px 12px',
                                    borderRadius: '16px',
                                    background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    textTransform: 'capitalize'
                                }}
                            >
                                Category: {filters.category}
                            </Tag>
                        )}
                        
                        {/* Clear All Button */}
                        {(searchInput || filters.status !== 'all' || filters.category !== 'all') && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    setSearchInput('');
                                    setFilters({ status: 'all', category: 'all' });
                                    setPagination(prev => ({ ...prev, current: 1 }));
                                    navigate('/search-results', { replace: true });
                                    loadSearchResults({ status: 'all', category: 'all' }, '', 1);
                                }}
                                style={{
                                    fontSize: '12px',
                                    padding: '0 8px',
                                    height: 'auto',
                                    color: '#64748b',
                                    fontWeight: 500
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Search Results Section */}
            <div className="search-results-section">
                {/* Header */}
                {/* <div style={{ 
                    marginBottom: 24,
                    padding: '20px',
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(24, 144, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <SearchOutlined style={{ 
                            fontSize: '20px', 
                            color: 'white' 
                        }} />
                        <Title level={3} style={{ 
                            margin: 0, 
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '20px'
                        }}>
                            Search Results
                        </Title>
                    </div>
                    <Text style={{ 
                        fontSize: '14px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 400
                    }}>
                        {searchInput ? `Found ${auctions.length} auction${auctions.length !== 1 ? 's' : ''} matching "${searchInput}"` : `Showing all ${auctions.length} auction${auctions.length !== 1 ? 's' : ''}`}
                    </Text>
                </div> */}

                {loading ? (
                    <div className="auctions-grid">
                        <Row gutter={[32, 32]}>
                            {[1, 2, 3, 4, 5, 6].map((item) => (
                                <Col xs={24} sm={12} md={8} key={item}>
                                    <AuctionCardSkeleton />
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : auctions.length === 0 ? (
                    <div className="empty-container">
                        <div className="empty-card">
                            <div style={{ 
                                fontSize: '64px', 
                                marginBottom: '24px',
                                opacity: 0.3
                            }}>
                                🔍
                            </div>
                            <Text className="empty-title">
                                No auctions found
                            </Text>
                            <Text className="empty-description">
                                {searchInput ? 
                                    `No auctions match "${searchInput}". Try adjusting your search terms or filters.` :
                                    'No auctions available. Try adjusting your filters or check back later.'
                                }
                            </Text>
                            <div style={{ marginTop: '24px' }}>
                                <Button 
                                    type="primary" 
                                    onClick={() => navigate('/auctions')}
                                    style={{
                                        borderRadius: '8px',
                                        height: '40px',
                                        padding: '0 24px',
                                        fontWeight: 500
                                    }}
                                >
                                    Browse All Auctions
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="auctions-grid">
                            <Row gutter={[32, 32]}>
                                {auctions.map(auction => (
                                    <Col xs={24} sm={12} md={8} key={auction.id}>
                                        <div style={{ position: 'relative' }}>
                                            <AuctionCard
                                                auction={auction}
                                                onFavoriteToggle={handleFavoriteToggle}
                                            />
                                            {/* Real-time countdown overlay */}
                                            <AuctionCountdown
                                                auction={auction}
                                                variant="overlay"
                                            />
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                        
                        {pagination.total > 0 && (
                            <div style={{ 
                                marginTop: 32,
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '24px 0'
                            }}>
                                <Pagination
                                    current={pagination.current}
                                    pageSize={pagination.pageSize}
                                    total={pagination.total}
                                    onChange={handlePaginationChange}
                                    showSizeChanger={false}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchResults;